import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Phone } from "lucide-react";
import { NAV_LINKS, CONTACT, scrollToSection } from "../lib/site";

const Logo = () => (
  <button
    data-testid="brand-logo"
    onClick={() => scrollToSection("home")}
    className="flex items-center group"
    aria-label="Coding In Store — home"
  >
    <img
      src="/logo-header.png"
      alt="Coding In Store logo"
      className="h-[45px] sm:h-[52px] w-auto shrink-0 bg-amber-500 rounded-lg border border-amber-500/25 shadow-[0_0_18px_rgba(245,158,11,0.18)]"
    />
    <span data-testid="brand-title" className="ml-3 sm:ml-4 text-left leading-none whitespace-nowrap">
      <span className="block font-display text-lg sm:text-xl font-semibold tracking-tight text-white">
        CODING <span className="text-amber-500 italic"> In Store</span>
      </span>
      <span className="block font-mono-x text-[9px] sm:text-[11px] tracking-[0.25em] text-white mt-1.5">
        YOUR ONLINE STORE
      </span>
    </span>
  </button>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("home");
  const go = (id) => {
    setOpen(false);
    setActive(id);
    setTimeout(() => scrollToSection(id), 80);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    NAV_LINKS.forEach((l) => {
      const el = document.getElementById(l.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#0b0c10]/85 border-b border-amber-500/15"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[96px] flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-10" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className={`font-mono-x text-s tracking-[0.25em] uppercase transition-colors duration-200 ${
                  active === l.id ? "text-amber-400" : "text-gray-400 hover:text-amber-400"
                }`}
              >
                {l.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              data-testid="header-cta-button"
              onClick={() => window.open("https://onlinekebab.store", "_blank", "noopener,noreferrer")}
              className="hidden sm:inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0b0c10] text-sm font-bold px-5 py-2.5 rounded-full shadow-[0_0_24px_rgba(245,158,11,0.35)] transition-colors duration-200"
            >
              Demo Link
              <ArrowUpRight className="w-4 h-4" />
            </button>
            <button
              data-testid="hamburger-menu-button"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              className="inline-flex lg:hidden items-center justify-center w-11 h-11 shrink-0 rounded-full border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 transition-colors duration-200"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              data-testid="drawer-backdrop"
              className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
            />
            <motion.aside
              data-testid="nav-drawer"
              className="fixed top-0 right-0 z-[70] h-full w-[86%] max-w-sm bg-[#12141c]/95 backdrop-blur-2xl border-l border-amber-500/20 flex flex-col"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 32 }}
            >
              <div className="flex items-center justify-between px-6 h-[96px] border-b border-amber-500/15">
                <Logo />
                <button
                  data-testid="drawer-close-button"
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-amber-500/30 text-amber-400 hover:bg-amber-500/10 transition-colors duration-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <nav className="flex-1 flex flex-col justify-center px-8 gap-2" aria-label="Drawer">
                {NAV_LINKS.map((l, i) => (
                  <motion.button
                    key={l.id}
                    data-testid={`drawer-link-${l.id}`}
                    onClick={() => go(l.id)}
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 * i + 0.1 }}
                    className="group flex items-baseline gap-4 py-4 border-b border-white/5 text-left"
                  >
                    <span className="font-mono-x text-xs text-amber-500">0{i + 1}</span>
                    <span
                      className={`font-display text-3xl font-semibold transition-colors duration-200 ${
                        active === l.id ? "text-amber-400" : "text-white group-hover:text-amber-400"
                      }`}
                    >
                      {l.label}
                    </span>
                  </motion.button>
                ))}
              </nav>
              <div className="px-8 pb-10 space-y-4">
                <a
                  data-testid="drawer-phone-link"
                  href={CONTACT.phoneHref}
                  className="flex items-center gap-3 text-gray-400 hover:text-amber-400 transition-colors duration-200"
                >
                  <Phone className="w-4 h-4 text-amber-500" />
                  <span className="font-mono-x text-sm">{CONTACT.phone}</span>
                </a>
                <button
                  data-testid="drawer-cta-button"
                  onClick={() => window.open("https://onlinekebab.store", "_blank", "noopener,noreferrer")}
                  className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-[#0b0c10] font-bold px-6 py-4 rounded-full shadow-[0_0_24px_rgba(245,158,11,0.35)] transition-colors duration-200"
                >
                  Demo Link
                  <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
